<script setup>
import { onMounted, ref, computed } from 'vue'
import { useStore } from 'vuex';
import { dispatchNotification } from './Notification';

const props = defineProps(['subscription', 'hasExpiredTrial', 'auth']);

const subscriptions = [
    {
        'name': 'Kostenloser Testzeitraum',
        'description': 'Teste alle Premium-Funktionen jetzt 30 Tage kostenlos!',
        'price': {
            'annually': 0.00,
            'semi-annually': 0.00
        }
    },
    {
        'name': 'Premium Paket',
        'description': 'Direkt Interessenten kontaktieren und komplette Stellenangebote einstellen.',
        'price': {
            'annually': 1290.00,
            'semi-annually': 790.00
        }
    },
    {
        'name': 'Premium-Plus Paket',
        'description': 'Alle Funktionen von Premium plus beste Sichtbarkeit im Portal.',
        'price': {
            'annually': 1390.00,
            'semi-annually': 890.00
        }
    }
]

const subscriptionNames = ['trial', 'premium', 'premium-plus'];

onMounted(() => {
    const selectedSubscriptionName = localStorage.getItem('subscription-name') ?? 'trial';
    form.value.subscription = subscriptionNames.indexOf(selectedSubscriptionName);
    form.value.annually = localStorage.getItem('subscription-annually') == 'true' ?? false;
});

const setsubscription = (index) => {
    form.value.subscription = index;
    localStorage.setItem('subscription-name', subscriptionNames[index]);
}

const store = useStore();
const csrfToken = store.state.csrfToken;

import {
    Dialog,
    DialogPanel,
    Popover,
    PopoverButton,
    PopoverGroup,
    PopoverPanel,
    RadioGroup,
    RadioGroupOption,
    Tab,
    TabGroup,
    TabList,
    TabPanel,
    TabPanels,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue'
import {
    Bars3Icon,
    MagnifyingGlassIcon,
    QuestionMarkCircleIcon,
    ShoppingBagIcon,
    XMarkIcon,
    InformationCircleIcon
} from '@heroicons/vue/24/outline'
import { CheckCircleIcon, ChevronDownIcon, TrashIcon } from '@heroicons/vue/20/solid'

const initialFormState = {
    annually: false,
    subscription: 0,
    salutation: '',
    name: '',
    street: '',
    housenr: '',
    plz: '',
    city: '',
    vatid: '',
    contactname: '',
    contacttel: '',
    contactmail: '',
    price: '',
    errors: '',
    processing: false,
};

const form = ref({ ...initialFormState });

const resetForm = () => {
    Object.assign(form.value, initialFormState);
}

const submit = () => {
    form.value.price = priceNet.value;
    form.value.processing = true;

    fetch('/api/subscriptions', {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(form.value)
    }).then(async (response) => {
        const json = await response.json();
        form.value.processing = false;

        if (response.status === 422) {
            form.value.errors = json.errors;
        } else if (response.status === 200) {
            resetForm();
            window.location = json.url;
        } else {
            dispatchNotification({ title: 'Es ist ein Fehler aufgetreten', content: 'Fehlercode: ' + response.status, type: 'error', duration: 5000 });
        }
    }).catch((error) => {
        console.log(error);
    });
};

const priceNet = computed(() => {
    return form.value.annually
        ? subscriptions[form.value.subscription]?.price.annually
        : subscriptions[form.value.subscription]?.price['semi-annually'];
});

const priceGross = computed(() => {
    return priceNet.value ? (priceNet.value * 1.19).toFixed(2) : 0;
});

const disabled = computed(() => {
    if (form.value.subscription === 0) {
        return props.hasExpiredTrial || ['trial', 'premium', 'premium-plus'].includes(props.subscription);
    }

    if (form.value.subscription === 1) {
        return ['premium', 'premium-plus'].includes(props.subscription);
    }

    return props.subscription === 'premium-plus';
});


</script>

<template>

    <div class="tw-mx-auto tw-max-w-4xl tw-text-center">
        <h2 class="tw-text-base tw-font-semibold tw-leading-7 tw-text-primary-500 tw-mb-0">Nur noch einen Schritt</h2>
        <p class="tw-mt-2 tw-text-4xl tw-font-bold tw-tracking-tight tw-text-gray-900 sm:tw-text-5xl">Jetzt Bestellung
            abschließen
        </p>
    </div>
    <p class="tw-mx-auto tw-mt-6 tw-max-w-2xl tw-text-center tw-text-lg tw-leading-8 tw-text-gray-600">
        Mit einer Premium Mitgliedschaft stehen Dir alle Möglichkeiten zur Verfügung mit Teilnehmerinnen und Teilnehmern
        in Kontakt zu treten und Deine Stellenangebote ausführlich und optimal zu platzieren.</p>

    <div class="tw-mt-16 tw-flex tw-justify-center">
        <fieldset
            class="tw-grid tw-grid-cols-2 tw-gap-x-1 tw-rounded-full tw-p-1 tw-text-center tw-text-xs tw-font-semibold tw-leading-5 tw-ring-1 tw-ring-inset tw-ring-gray-200">
            <legend class="tw-sr-only">Payment frequency</legend>
            <label class="tw-cursor-pointer tw-rounded-full tw-px-4 tw-py-1 tw-mb-0"
                :class="[!form.annually ? 'tw-bg-primary-500 tw-text-white' : 'tw-text-gray-500']">
                <input type="radio" :value="false" class="tw-sr-only" v-model="form.annually">
                <span>Halbjährlich</span>
            </label>
            <label class="tw-cursor-pointer tw-rounded-full tw-px-4 tw-py-1 tw-mb-0"
                :class="[form.annually ? 'tw-bg-primary-500 tw-text-white' : 'tw-text-gray-500']">
                <input type="radio" :value="true" class="tw-sr-only" v-model="form.annually">
                <span>Jährlich</span>
            </label>
        </fieldset>
    </div>

    <div
        class="isolate tw-mx-auto tw-grid tw-max-w-md tw-grid-cols-1 tw-gap-y-8 tw-mt-10 lg:tw-mx-0 lg:tw-max-w-none lg:tw-grid-cols-3">
        <div @click="setsubscription(0)"
            class="tw-cursor-pointer tw-flex tw-flex-col tw-justify-between tw-rounded-3xl tw-bg-white tw-p-8 tw-ring-1 tw-ring-gray-200 xl:tw-p-10 lg:tw-mt-8 lg:tw-rounded-r-none"
            :class="form.subscription == 0 ? 'tw-ring-primary-500 tw-ring-4 lg:tw-z-10' : ''">
            <div>
                <div class="tw-flex tw-items-center tw-justify-between tw-gap-x-4">
                    <h3 class="tw-text-lg tw-font-semibold tw-leading-8 tw-text-gray-900">Jetzt kostenlos Premium testen
                        ✨</h3>
                </div>
                <p class="tw-mt-4 tw-text-sm tw-leading-6 tw-text-gray-600">Teste alle Premium-Funktionen jetzt 30 Tage
                    kostenlos! Keine Zahlungsinformationen erforderlich, das Paket endet automatisch – ganz ohne
                    Verpflichtungen.
                </p>
                <p class="tw-mt-6 tw-flex tw-items-baseline tw-gap-x-1">
                    <span class="tw-text-4xl tw-font-bold tw-tracking-tight tw-text-gray-900">€0</span>
                    <span class="tw-text-sm tw-font-semibold tw-leading-6 tw-text-gray-600">für 30
                        Tage</span>
                </p>
                <ul role="list" class="tw-mt-8 tw-space-y-3 tw-text-sm tw-leading-6 tw-text-gray-600 tw-pl-0">
                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Alle Funktionen von Premium
                    </li>
                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Endet automatisch
                    </li>
                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Keine Zahlungsinformationen erforderlich
                    </li>
                </ul>
            </div>

            <template v-if="props.hasExpiredTrial">
                <span
                    class="tw-transition tw-cursor-pointer !tw-no-underline tw-mt-8 tw-block tw-rounded-md tw-py-2 tw-px-3 tw-text-center tw-text-sm tw-font-semibold tw-leading-6 !tw-text-gray-400 tw-ring-1 tw-ring-inset tw-ring-gray-200">Testzeitraum
                    abgelaufen</span>
            </template>
            <template v-else-if="props.subscription == 'trial'">
                <span
                    class="tw-transition tw-cursor-pointer !tw-no-underline tw-mt-8 tw-block tw-rounded-md tw-py-2 tw-px-3 tw-text-center tw-text-sm tw-font-semibold tw-leading-6 !tw-text-gray-400 tw-ring-1 tw-ring-inset tw-ring-gray-200">Aktiv</span>
            </template>
            <template v-else>
                <span v-if="form.subscription == 0"
                    class="tw-transition tw-cursor-pointer !tw-no-underline tw-mt-8 tw-block tw-rounded-md tw-py-2 tw-px-3 tw-text-center tw-text-sm tw-font-semibold tw-leading-6 !tw-text-gray-400 tw-ring-1 tw-ring-inset tw-ring-gray-200">Ausgewählt</span>
                <span v-else
                    class="tw-transition tw-cursor-pointer !tw-no-underline tw-mt-8 tw-block tw-rounded-md tw-py-2 tw-px-3 tw-text-center tw-text-sm tw-font-semibold tw-leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 tw-text-primary-500 tw-ring-1 tw-ring-inset tw-ring-primary-200 hover:tw-ring-primary-300 hover:tw-bg-primary-500 hover:tw-text-white hover:tw-ring-white">Auswählen</span>
            </template>

        </div>
        <div @click="setsubscription(1)"
            class="tw-cursor-pointer tw-flex tw-flex-col tw-justify-between tw-rounded-3xl tw-bg-white tw-p-8 tw-ring-1 tw-ring-gray-200 xl:tw-p-10 lg:tw-rounded-b-none"
            :class="form.subscription == 1 ? 'tw-ring-primary-500 tw-ring-4 lg:tw-z-10' : ''">
            <div>
                <div class="tw-flex tw-items-center tw-justify-between tw-gap-x-4">
                    <h3 class="tw-text-lg tw-font-semibold tw-leading-8 tw-text-primary-500">
                        Premium</h3>
                    <p
                        class="tw-rounded-full tw-bg-primary-500/10 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-leading-5 tw-text-primary-500">
                        Beliebt</p>
                </div>
                <p class="tw-mt-4 tw-text-sm tw-leading-6 tw-text-gray-600">Direkt Interessenten kontaktieren und
                    komplette
                    Stellenangebote einstellen.</p>

                <p v-if="form.annually" class="tw-mt-6 tw-flex tw-items-baseline tw-gap-x-1">
                    <span class="tw-text-4xl tw-font-bold tw-tracking-tight tw-text-gray-900">
                        €1290
                    </span>
                    <span class="tw-text-sm tw-font-semibold tw-leading-6 tw-text-gray-600">für 12
                        Monate (ohne MwSt.)</span>
                </p>

                <p v-else class="tw-mt-6 tw-flex tw-items-baseline tw-gap-x-1">
                    <span class="tw-text-4xl tw-font-bold tw-tracking-tight tw-text-gray-900">
                        €790
                    </span>
                    <span class="tw-text-sm tw-font-semibold tw-leading-6 tw-text-gray-600">für 6
                        Monate (ohne MwSt.)</span>
                </p>

                <ul role="list" class="tw-mt-8 tw-space-y-3 tw-text-sm tw-leading-6 tw-text-gray-600 tw-pl-0">
                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Unternehmensprofil anlegen
                    </li>
                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Stellenangebote mit Detail-Seite
                    </li>
                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Live-Chat im Messenger
                    </li>

                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Zugriff auf alle Interessenten
                    </li>
                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Matching-Mails bei neuen Interessenten
                    </li>
                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Alle Benefits deines Unternehmens
                    </li>
                </ul>
            </div>


            <template v-if="props.subscription == 'premium'">
                <span
                    class="tw-transition tw-cursor-pointer !tw-no-underline tw-mt-8 tw-block tw-rounded-md tw-py-2 tw-px-3 tw-text-center tw-text-sm tw-font-semibold tw-leading-6 !tw-text-gray-400 tw-ring-1 tw-ring-inset tw-ring-gray-200">Aktiv</span>
            </template>
            <template v-else>
                <span v-if="form.subscription == 1"
                    class="tw-transition tw-cursor-pointer !tw-no-underline tw-mt-8 tw-block tw-rounded-md tw-py-2 tw-px-3 tw-text-center tw-text-sm tw-font-semibold tw-leading-6 !tw-text-gray-400 tw-ring-1 tw-ring-inset tw-ring-gray-200">Ausgewählt</span>
                <span v-else
                    class="tw-transition tw-cursor-pointer !tw-no-underline tw-mt-8 tw-block tw-rounded-md tw-py-2 tw-px-3 tw-text-center tw-text-sm tw-font-semibold tw-leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 tw-text-primary-500 tw-ring-1 tw-ring-inset tw-ring-primary-200 hover:tw-ring-primary-300 hover:tw-bg-primary-500 hover:tw-text-white hover:tw-ring-white">Auswählen</span>
            </template>

        </div>
        <div @click="setsubscription(2)"
            class="tw-cursor-pointer tw-flex tw-flex-col tw-justify-between tw-rounded-3xl tw-bg-white tw-p-8 tw-ring-1 tw-ring-gray-200 xl:tw-p-10 lg:tw-mt-8 lg:tw-rounded-l-none"
            :class="form.subscription == 2 ? 'tw-ring-primary-500 tw-ring-4 lg:tw-z-10' : ''">
            <div>
                <div class="tw-flex tw-items-center tw-justify-between tw-gap-x-4">
                    <h3 class="tw-text-lg tw-font-semibold tw-leading-8 tw-text-gray-900">
                        Premium-Plus</h3>
                </div>
                <p class="tw-mt-4 tw-text-sm tw-leading-6 tw-text-gray-600">Alle Funktionen von Premium plus
                    beste
                    Sichtbarkeit im Portal.</p>
                <p v-if="form.annually" class="tw-mt-6 tw-flex tw-items-baseline tw-gap-x-1">
                    <span class="tw-text-4xl tw-font-bold tw-tracking-tight tw-text-gray-900">
                        €1390
                    </span>
                    <span class="tw-text-sm tw-font-semibold tw-leading-6 tw-text-gray-600">für 12
                        Monate (ohne MwSt.)</span>
                </p>

                <p v-else class="tw-mt-6 tw-flex tw-items-baseline tw-gap-x-1">
                    <span class="tw-text-4xl tw-font-bold tw-tracking-tight tw-text-gray-900">
                        €890
                    </span>
                    <span class="tw-text-sm tw-font-semibold tw-leading-6 tw-text-gray-600">für 6
                        Monate (ohne MwSt.)</span>
                </p>

                <ul role="list" class="tw-mt-8 tw-space-y-3 tw-text-sm tw-leading-6 tw-text-gray-600 tw-pl-0">
                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Unternehmensprofil anlegen
                    </li>
                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Stellenangebote mit Detail-Seite
                    </li>
                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Live-Chat im Messenger
                    </li>

                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Zugriff auf alle Interessenten
                    </li>
                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Matching-Mails bei neuen Interessenten
                    </li>
                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Alle Benefits deines Unternehmens
                    </li>
                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Sticker "TOP-Arbeitgeber"
                    </li>
                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Platzierung auf der Startseite
                    </li>
                    <li class="tw-flex tw-gap-x-3">
                        <svg class="tw-h-6 tw-w-5 tw-flex-none tw-text-primary-500" viewBox="0 0 20 20"
                            fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd" />
                        </svg>
                        Besondere Sichtbarkeit in den Stellenangeboten
                    </li>
                </ul>
            </div>

            <template v-if="props.subscription == 'premium-plus'">
                <span
                    class="tw-transition tw-cursor-pointer !tw-no-underline tw-mt-8 tw-block tw-rounded-md tw-py-2 tw-px-3 tw-text-center tw-text-sm tw-font-semibold tw-leading-6 !tw-text-gray-400 tw-ring-1 tw-ring-inset tw-ring-gray-200">Aktiv</span>
            </template>
            <template v-else>
                <span v-if="form.subscription == 2"
                    class="tw-transition tw-cursor-pointer !tw-no-underline tw-mt-8 tw-block tw-rounded-md tw-py-2 tw-px-3 tw-text-center tw-text-sm tw-font-semibold tw-leading-6 !tw-text-gray-400 tw-ring-1 tw-ring-inset tw-ring-gray-200">Ausgewählt</span>
                <span v-else
                    class="tw-transition tw-cursor-pointer !tw-no-underline tw-mt-8 tw-block tw-rounded-md tw-py-2 tw-px-3 tw-text-center tw-text-sm tw-font-semibold tw-leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 tw-text-primary-500 tw-ring-1 tw-ring-inset tw-ring-primary-200 hover:tw-ring-primary-300 hover:tw-bg-primary-500 hover:tw-text-white hover:tw-ring-white">Auswählen</span>
            </template>
        </div>
    </div>

    <main class="tw-pb-24 tw-pt-16">
        <div class="tw-mx-auto tw-max-w-2xl lg:tw-max-w-none">

            <form @submit.prevent="submit" class="lg:tw-grid lg:tw-grid-cols-5 lg:tw-gap-x-12 xl:tw-gap-x-16">

                <div v-if="form.subscription == 0"
                    class="tw-col-span-3 tw-flex tw-items-center tw-justify-center tw-border tw-border-dashed tw-rounded-3xl tw-border-gray-300 tw-bg-gray-100 tw-flex-col tw-text-gray-400">
                    <InformationCircleIcon class="tw-w-10 tw-mb-5" />
                    <span class="tw-max-w-md tw-text-center">Für den kostenlosen Testzeitraum benötigen wir keine
                        Rechnungsinformationen von
                        Dir.</span>
                </div>
                <div v-else class="tw-col-span-3">

                    <div>
                        <h3>Rechnungsempfänger</h3>

                        <div class="tw-mt-4 tw-grid tw-grid-cols-1 sm:tw-grid-cols-12 sm:tw-gap-x-4">

                            <div class="sm:tw-col-span-12">
                                <div class="form-group">
                                    <label for="salutation">Anrede *</label>
                                    <select v-model="form.salutation" id="salutation" class="custom-select"
                                        :class="{ 'is-invalid': form.errors.salutation }" required>
                                        <option value="">Bitte auswählen...</option>
                                        <option value="Frau">Frau</option>
                                        <option value="Herr">Herr</option>
                                        <option value="Firma">Firma</option>
                                    </select>

                                    <div v-if="form.errors.salutation && form.errors.salutation.length"
                                        class="invalid-feedback" role="alert">
                                        {{ form.errors.salutation[0] }}
                                    </div>
                                </div>
                            </div>

                            <div class="sm:tw-col-span-12">
                                <div class="form-group">
                                    <label for="name">Name *</label>
                                    <input id="name" type="text" v-model="form.name" class="form-control"
                                        :class="{ 'is-invalid': form.errors.name }" placeholder="Name" required>

                                    <div v-if="form.errors.name && form.errors.name.length" class="invalid-feedback"
                                        role="alert">
                                        {{ form.errors.name[0] }}
                                    </div>
                                </div>
                            </div>

                            <div class="sm:tw-col-span-8">
                                <div class="form-group">
                                    <label for="street">Straße *</label>
                                    <input id="street" type="text" v-model="form.street" class="form-control"
                                        :class="{ 'is-invalid': form.errors.street }" placeholder="Straße" required>

                                    <div v-if="form.errors.street && form.errors.street.length" class="invalid-feedback"
                                        role="alert">
                                        {{ form.errors.street[0] }}
                                    </div>
                                </div>
                            </div>

                            <div class="sm:tw-col-span-4">
                                <div class="form-group">
                                    <label for="housenr">Hausnummer *</label>
                                    <input id="housenr" type="text" v-model="form.housenr" class="form-control"
                                        :class="{ 'is-invalid': form.errors.housenr }" placeholder="Hausnummer"
                                        required>

                                    <div v-if="form.errors.housenr && form.errors.housenr.length"
                                        class="invalid-feedback" role="alert">
                                        {{ form.errors.housenr[0] }}
                                    </div>
                                </div>
                            </div>

                            <div class="sm:tw-col-span-4">
                                <div class="form-group">
                                    <label for="plz">PLZ *</label>
                                    <input id="plz" type="text" v-model="form.plz" class="form-control"
                                        :class="{ 'is-invalid': form.errors.plz }" placeholder="PLZ" required>

                                    <div v-if="form.errors.plz && form.errors.plz.length" class="invalid-feedback"
                                        role="alert">
                                        {{ form.errors.plz[0] }}
                                    </div>
                                </div>
                            </div>

                            <div class="sm:tw-col-span-8">
                                <div class="form-group">
                                    <label for="city">Stadt *</label>
                                    <input id="city" type="text" v-model="form.city" class="form-control"
                                        :class="{ 'is-invalid': form.errors.city }" placeholder="Stadt" required>

                                    <div v-if="form.errors.city && form.errors.city.length" class="invalid-feedback"
                                        role="alert">
                                        {{ form.errors.city[0] }}
                                    </div>
                                </div>
                            </div>

                            <div class="sm:tw-col-span-12">
                                <div class="form-group">
                                    <label for="vatid">Umsatzsteuer-ID</label>
                                    <input id="vatid" type="text" v-model="form.vatid" class="form-control"
                                        :class="{ 'is-invalid': form.errors.vatid }" placeholder="Umsatzsteuer-ID">

                                    <div v-if="form.errors.vatid && form.errors.vatid.length" class="invalid-feedback"
                                        role="alert">
                                        {{ form.errors.vatid[0] }}
                                    </div>
                                </div>
                            </div>

                            <h3 class="tw-mt-8">Ansprechpartner</h3>

                            <div class="sm:tw-col-span-12">
                                <div class="form-group">
                                    <label for="contactname">Name *</label>
                                    <input id="contactname" type="text" v-model="form.contactname" class="form-control"
                                        :class="{ 'is-invalid': form.errors.contactname }" placeholder="Name" required>

                                    <div v-if="form.errors.contactname && form.errors.contactname.length"
                                        class="invalid-feedback" role="alert">
                                        {{ form.errors.contactname[0] }}
                                    </div>
                                </div>
                            </div>

                            <div class="sm:tw-col-span-6">
                                <div class="form-group">
                                    <label for="contacttel">Telefonnummer *</label>
                                    <input id="contacttel" type="tel" v-model="form.contacttel" class="form-control"
                                        :class="{ 'is-invalid': form.errors.contacttel }" placeholder="Telefonnummer"
                                        required>

                                    <div v-if="form.errors.contacttel && form.errors.contacttel.length"
                                        class="invalid-feedback" role="alert">
                                        {{ form.errors.contacttel[0] }}
                                    </div>
                                </div>
                            </div>

                            <div class="sm:tw-col-span-6">
                                <div class="form-group">
                                    <label for="contactmail">E-Mail Adresse *</label>
                                    <input id="contactmail" type="tel" v-model="form.contactmail" class="form-control"
                                        :class="{ 'is-invalid': form.errors.contactmail }" placeholder="E-Mail Adresse"
                                        required>

                                    <div v-if="form.errors.contactmail && form.errors.contactmail.length"
                                        class="invalid-feedback" role="alert">
                                        {{ form.errors.contactmail[0] }}
                                    </div>
                                </div>
                            </div>

                            <div class="sm:tw-col-span-12">
                                Mit * markierte Felder sind Pflichtfelder.
                            </div>

                        </div>
                    </div>
                </div>

                <!-- Order summary -->
                <div class="tw-mt-10 lg:tw-mt-0 tw-col-span-2">

                    <div class="tw-rounded-3xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm">

                        <div
                            class="tw-p-6 tw-flex tw-flex-1 tw-flex-col tw-border tw-border-primary-500 tw-m-6 tw-rounded-xl tw-relative">

                            <CheckCircleIcon class="tw-h-5 tw-w-5 tw-text-primary-500 tw-absolute tw-top-5 tw-right-5"
                                aria-hidden="true" />

                            <div class="tw-flex">
                                <div class="tw-min-w-0 tw-flex-1">
                                    <h4 class="tw-text-sm tw-mb-0">
                                        <span class="tw-font-medium tw-text-gray-900 hover:tw-text-gray-800">{{
                                            subscriptions[form.subscription]?.name }}</span>
                                    </h4>
                                    <p class="tw-text-sm tw-text-gray-500">{{
                                        subscriptions[form.subscription]?.description }}</p>
                                </div>
                            </div>

                            <div class="tw-flex tw-flex-1 tw-items-end tw-justify-between">
                                <p v-if="form.annually && form.subscription != 0"
                                    class="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-0">12 Monate
                                    für €
                                    {{
                                        Number(priceNet || 0)
                                            .toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                    }} (ohne MwSt.)
                                </p>
                                <p v-else-if="!form.annually && form.subscription != 0"
                                    class="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-0">6 Monate für €
                                    {{
                                        Number(priceNet || 0)
                                            .toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                    }} (ohne MwSt.)
                                </p>

                                <p v-else class="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-0">30 Tage für €
                                    {{
                                        Number(priceNet || 0)
                                            .toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                    }}
                                </p>
                            </div>
                        </div>

                        <dl class="tw-space-y-1 tw-border-gray-200 tw-px-4 tw-py-6 sm:tw-px-6">
                            <div class="tw-flex tw-items-center tw-justify-between">
                                <dt class="tw-text-sm">Summe (netto)</dt>
                                <dd class="tw-text-sm tw-font-medium tw-text-gray-900">€ {{ Number(priceNet || 0)
                                    .toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                                </dd>
                            </div>
                            <div class="tw-flex tw-items-center tw-justify-between tw-text-gray-500">
                                <dt class="tw-text-sm">MwSt. (19,00%)</dt>
                                <dd class="tw-text-sm tw-font-medium">€ {{ Number((priceNet || 0) * 0.19)
                                    .toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                                </dd>
                            </div>
                            <div
                                class="tw-flex tw-items-center tw-justify-between tw-border-t tw-border-gray-200 tw-pt-6">
                                <dt class="tw-text-lg tw-font-medium">Summe (brutto)</dt>
                                <dd class="tw-text-lg tw-font-medium tw-text-gray-900">€ {{ Number(priceGross || 0)
                                    .toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                                </dd>
                            </div>
                        </dl>

                        <div class="tw-border-t tw-border-gray-200 tw-px-4 tw-py-6 sm:tw-px-6">

                            <div class="form-check">
                                <input id="agb" class="form-check-input" type="checkbox" required>
                                <label class="form-check-label" for="agb">
                                    Ich akzeptiere die <a href="/agb">AGB</a>. *
                                </label>
                            </div>

                            <div class="form-check tw-mb-4">
                                <input id="commercial" class="form-check-input" type="checkbox" required>
                                <label class="form-check-label" for="commercial">
                                    Ich bestätige, dass ich gewerblich und nicht als Verbraucher handle. *
                                </label>
                            </div>

                            <div class="tw-relative">
                                <button type="submit" :disabled="disabled || form.processing"
                                    class="tw-relative tw-transition tw-w-full tw-rounded-md tw-border tw-border-transparent tw-bg-primary-500 tw-px-4 tw-py-3 tw-text-base tw-font-medium tw-text-white tw-shadow-sm hover:tw-bg-primary-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-500 focus:tw-ring-offset-2 focus:tw-ring-offset-gray-50">
                                    <span>
                                        {{ form.subscription == 0 ? 'Jetzt aktivieren' : 'Kostenpflichtig bestellen' }}
                                    </span>
                                </button>

                                <div v-if="form.processing"
                                    class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-gray-900 tw-opacity-50 tw-rounded-md">
                                    <svg class="tw-animate-spin tw-h-6 tw-w-6 tw-text-primary-500"
                                        xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                            stroke-width="4"></circle>
                                        <path class="tw-opacity-75" fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                        </path>
                                    </svg>
                                </div>
                            </div>

                            <p v-if="disabled" class="tw-text-red-500 tw-text-sm tw-mt-4 tw-mb-0">
                                <InformationCircleIcon class="tw-w-5" />
                                Dieses Paket kann nicht gebucht werden, da es entweder bereits aktiv oder für dein
                                Benutzerkonto nicht verfügbar ist.
                            </p>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </main>

</template>
